import { trackCategoriesResultsScreenView } from "@faire/web-api/events/categories/view/resultsScreen";
import { trackSignupModalOpenCatnavBottomCtaClick } from "@faire/web-api/events/signup/click/modalOpenCatnavBottomCta";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { Suspense, useEffect } from "react";
import { useHistory } from "react-router-dom";

import {
  recordLandingActivity,
  recordSignupActivity,
} from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { Footer } from "@faire/retailer/components/Footer";
import { KeepExploringCarousel } from "@faire/retailer/components/KeepExploringCarousel";
import { LazyLoggedOutMobileSignUpSnackbar } from "@faire/retailer/components/LoggedOutMobileSignUpSnackbar";
import { SignUpBanner } from "@faire/retailer/components/SignUpBanner/SignUpBanner";
import { PaddedContainer } from "@faire/retailer/features/Marketplace/style";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { useAssignSettingLoggedOutCategoryPageRedesign } from "@faire/retailer/settings/useSettingLoggedOutCategoryPageRedesign";
import { useCategoryPageScroll } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageScroll";
import { LoggedOutCategoryPageAboveFold } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageAboveFold/LoggedOutCategoryPageAboveFold";
import { LoggedOutCategoryPageBrandReviewsCarousel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageBrandReviewsCarousel";
import { LoggedOutCategoryPageFAQs } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/LoggedOutCategoryPageFAQs";
import { LoggedOutCategoryPageMoreInC3Pills } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageMoreInC3Pills/LoggedOutCategoryPageMoreInC3Pills";
import { LoggedOutCategoryPageRelatedSearches } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageRelatedSearches";
import { LoggedOutCategoryPageSeoTextBlock } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageSeoTextBlock";
import { LoggedOutCategoryPagePagination } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPagePagination";
import { LoggedOutCategoryPageLayoutElements } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageLayoutElements/LoggedOutCategoryPageLayoutElements";
import { VisibilityTracker } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/VisibilityTracker";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useLoggedOutCategoryPageCurrentNavigationPath } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentNavigationPath";

export const LoggedOutCategoryPageBase: React.FC<{
  categoryPageLoadTimingRef?: React.Ref<HTMLDivElement>;
}> = ({ categoryPageLoadTimingRef }) => {
  const history = useHistory();

  const categoryModel = useLoggedOutCategoryPageCurrentCategory();
  const navigationPath = useLoggedOutCategoryPageCurrentNavigationPath();

  const { scrollToTop } = useCategoryPageScroll();

  const assignSettingLoggedOutCategoryPageRedesign =
    useAssignSettingLoggedOutCategoryPageRedesign();

  useEffect(() => {
    assignSettingLoggedOutCategoryPageRedesign();
  }, [assignSettingLoggedOutCategoryPageRedesign]);

  const handleSignUp = () =>
    popSignUp(SignUpType.GENERIC, trackSignupModalOpenCatnavBottomCtaClick, {
      element: SignUpElement.BELOW_FOLD,
      sourceId: "logged_out_category_page_signup_banner",
    });

  useEffect(() => {
    recordLandingActivity(
      {
        category_path_identifier: categoryModel?.mostSpecificPathIdentifier,
      },
      history
    );

    try {
      recordSignupActivity(
        {
          category_path_identifier: categoryModel?.mostSpecificPathIdentifier,
        },
        getLocationOrThrow().pathname
      );
    } catch (e) {
      // If there's no window.location don't record
    }
  }, [categoryModel?.mostSpecificPathIdentifier, history]);

  useEffect(() => {
    trackCategoriesResultsScreenView(navigationPath);
  }, [navigationPath]);

  if (!categoryModel) {
    return null;
  }

  return (
    <>
      <Column>
        <LoggedOutCategoryPageAboveFold
          categoryPageLoadTimingRef={categoryPageLoadTimingRef}
        />
        <LoggedOutCategoryPagePagination scrollToTop={scrollToTop} />
        <PaddedContainer $noPaddingOnMobile>
          <Spacer size="5x" />
          <WhenVisibilityIsImminent>
            <LoggedOutCategoryPageLayoutElements
              placement={[ILayoutElement.Placement.BOTTOM]}
            />
            <LoggedOutCategoryPageMoreInC3Pills />
          </WhenVisibilityIsImminent>
          <LoggedOutCategoryPageRelatedSearches />
          <LoggedOutCategoryPageBrandReviewsCarousel />
          <VisibilityTracker name="keep-exploring-carousel">
            <KeepExploringCarousel
              name="logged-out-category-keep-exploring"
              clickTrackPath="logged-out-category/keep-exploring-carousel"
              removeCategoryPath={categoryModel.pathIdentifier}
              onClick={() => {
                getWindowOrThrow().scrollTo?.(0, 0);
              }}
            />
          </VisibilityTracker>

          <LoggedOutCategoryPageFAQs />
        </PaddedContainer>
        <VisibilityTracker name="signup-banner">
          <SignUpBanner onClick={handleSignUp} />
        </VisibilityTracker>
        <LoggedOutCategoryPageSeoTextBlock />
        <Footer />
      </Column>

      <Suspense fallback={null}>
        <LazyLoggedOutMobileSignUpSnackbar />
      </Suspense>
    </>
  );
};
